import { h, Component, ComponentChildren } from "preact";

import classname from "../../foundation/utils/classname";
import { stopPropagation } from "../../foundation/utils/legacy";
import noderef from "../../foundation/utils/noderef";
import NodeRefs from "../../foundation/utils/NodeRefs";
import { isMobileViewport } from "../../foundation/utils/ua-utils";

import styles from "./Sidebar.module.css";

type HorizontalDirection = "left" | "right";
type MouseEventName = "pointerdown" | "pointermove" | "pointerup" | "touchstart" | "touchmove" | "touchend" | "mousedown" | "mousemove" | "mouseup";
type MouseEventDirection = "down" | "move" | "up";
type MouseEventSource = "pointer" | "touch" | "mouse";

type IMouseEventDictionary = {
    [key in MouseEventSource]: {
        [key in MouseEventDirection]: MouseEventName;
    };
};

const MOUSE_EVENTS: IMouseEventDictionary = {
    pointer: {
        down: "pointerdown",
        move: "pointermove",
        up: "pointerup"
    },
    touch: {
        down: "touchstart",
        move: "touchmove",
        up: "touchend"
    },
    mouse: {
        down: "mousedown",
        move: "mousemove",
        up: "mouseup"
    }
};

function getMouseEventSource() {
    if ("PointerEvent" in window) {
        return "pointer";
    } else if ("ontouchstart" in document) {
        return "touch";
    }

    return "mouse";
}

function getMouseEvent(type: MouseEventDirection): MouseEventName {
    const eventSource = getMouseEventSource();

    return MOUSE_EVENTS[eventSource][type];
}

interface DefaultProps {
    collapsible: boolean;
    collapsed: boolean;
    initialWidth: number;
    minWidth: number;
    maxWidth: number;
    resizingSide: HorizontalDirection;
}

interface Props extends Partial<DefaultProps> {
    class?: string;
    "data-selector"?: string;
    contentClass?: string;
    compact?: boolean;
    collapsible: boolean;
    collapsed: boolean;
    initialWidth: number;
    minWidth: number;
    maxWidth: number;
    resizingSide?: HorizontalDirection;
    noScroll?: boolean;
    disableResize?: boolean;
    onRelease?: (width: number, o: {collapsed: boolean;}) => void;
    onResize?: (width: number) => void;
    onCollapse?: (lastSidebarWidth: number) => void;
    onExpand?: () => void;
    paddingBottom?: number;
    onClick?: (ev: MouseEvent) => void;
    onMouseDown?: (ev: Event) => void;
    allowOverflow?: boolean;
    contentRef?: (ref: HTMLDivElement) => void;
    baseRef?: (ref: HTMLElement) => void;
    children?: ComponentChildren;
}

interface State {
    width: number;
    collapsed: boolean;
    resizing: boolean;
}

const SidebarLoading = ({ width }: { width: number; }) => (
    <div class={classname(styles.sidebar, styles.loading)} style={{ width }}/>
);

/**
 * Resizable sidebar component which can be used in collapsed and compacted modes (see examples).
 *
 * Its styling can be customized using the following CSS variables:
 *  - `--sidebar-collapsed-width` (default: `20px`): Changes width of sidebar's resizer when it is collapsed.
 *  - `--sidebar-compact-width` (default: `40px`): Changes width of sidebar in compact mode.
 *  - `--sidebar-resizer-bg-color` (default: casper): Changes the background color of the resizer.
 *  - `--sidebar-bg-color` (default: casper): Changes the background color of the sidebar.
 */
class Sidebar extends Component<Props, State> {
    static Loading = SidebarLoading;
    static override defaultProps: DefaultProps = {
        collapsible: true,
        collapsed: isMobileViewport(),
        initialWidth: 0,
        minWidth: 0,
        maxWidth: Infinity,
        resizingSide: "left"
    };

    nodeRefs: NodeRefs = {};

    constructor(props : Props) {
        super(props);

        const {
            initialWidth, minWidth, maxWidth, collapsible, collapsed
        } = props;

        this.state = {
            width: Math.min(Math.max(initialWidth, minWidth), maxWidth),
            collapsed: collapsible && collapsed,
            resizing: false
        };
    }

    collapse = () => {
        const { onCollapse } = this.props;
        const { width } = this.state;

        if (onCollapse) {
            onCollapse(width);
        }

        this.setState({
            collapsed: true
        });
    };

    expand = () => {
        const { onExpand } = this.props;
        if (onExpand) {
            onExpand();
        }

        this.setState({
            collapsed: false
        });
    };

    handleBaseRef = (el: HTMLDivElement | null) => {
        noderef(this, "base")(el);
        if (this.props.baseRef && el) {
            this.props.baseRef(el);
        }
    };

    handleContentRef = (el: HTMLDivElement | null) => {
        if (this.props.contentRef && el) {
            this.props.contentRef(el);
        }
    };

    handleMouseMove = (ev: MouseEvent | TouchEvent) => {
        ev.preventDefault();

        requestAnimationFrame(() => {
            const { base } = this.nodeRefs;
            if (!base) {
                return;
            }

            const {
                minWidth,
                maxWidth,
                resizingSide,
                collapsible,
                onCollapse,
                onExpand
            } = this.props;
            const { collapsed: wasCollapsed } = this.state;
            const { left, right } = base.getBoundingClientRect();
            const point = (ev instanceof MouseEvent) ? ev.clientX : ev.changedTouches[0].clientX;
            if (!point) {
                return;
            }

            const pointDistance = resizingSide === "right" ? point - left : right - point;
            const collapsed = collapsible && pointDistance < minWidth / 2;
            const width = Math.min(Math.max(pointDistance, minWidth), maxWidth);

            this.setState({
                collapsed,
                width
            });

            if (!wasCollapsed && collapsed && onCollapse) {
                onCollapse(minWidth);
            } else if (wasCollapsed && !collapsed && onExpand) {
                onExpand();
            }
        });
    };

    handleMouseUp = (ev: MouseEvent | TouchEvent) => {
        document.removeEventListener(getMouseEvent("move"), this.handleMouseMove);
        document.removeEventListener(getMouseEvent("up"), this.handleMouseUp);

        ev.stopPropagation();

        requestAnimationFrame(() => {
            const { base } = this.nodeRefs;
            if (!base) {
                return;
            }
            const { onRelease } = this.props;
            const { collapsed } = this.state;
            const width = base.clientWidth;

            document.body.classList.remove(styles.resizing);
            delete document.body.dataset["resizeCursor"];

            if (onRelease) {
                onRelease(width, { collapsed });
            }

            this.setState({
                width,
                resizing: false
            });
        });
    };

    handleResizerMouseDown = () => {
        document.addEventListener(getMouseEvent("move"), this.handleMouseMove);
        document.addEventListener(getMouseEvent("up"), this.handleMouseUp);
        document.addEventListener("click", this.handleResizingClick, true);

        document.body.classList.add(styles.resizing);

        this.setState({ resizing: true });
    };

    handleResizingClick = (ev: MouseEvent) => {
        ev.stopImmediatePropagation();
        ev.stopPropagation();

        document.removeEventListener("click", this.handleResizingClick, true);
    };

    override componentDidMount() {
        const { disableResize } = this.props;
        if (!disableResize) {
            this.nodeRefs["resizer"]!.addEventListener(getMouseEvent("down"), this.handleResizerMouseDown);
        }
    }

    override componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.onResize && this.state.resizing && prevState.width !== this.state.width) {
            this.props.onResize(this.nodeRefs["base"]!.clientWidth);
        }
    }

    override componentWillUnmount() {
        const { disableResize } = this.props;

        document.removeEventListener(getMouseEvent("move"), this.handleMouseMove);
        document.removeEventListener(getMouseEvent("up"), this.handleMouseUp);

        if (!disableResize) {
            this.nodeRefs["resizer"]!.removeEventListener(getMouseEvent("down"), this.handleResizerMouseDown);
        }
    }

    render() {
        const { collapsed, resizing, width } = this.state;
        const {
            children,
            class: cssClass,
            contentClass,
            resizingSide = "left",
            compact,
            minWidth,
            paddingBottom,
            noScroll,
            allowOverflow,
            onClick,
            onMouseDown,
            disableResize
        } = this.props;
        const style = {};
        const classnames = classname(
            styles.sidebar,
            cssClass,
            styles[resizingSide], {
                [styles.resizable]: !disableResize,
                [styles.resizing]: resizing,
                [styles.collapsed]: collapsed,
                [styles.compact]: compact
            }
        );

        if (!collapsed) {
            // Should apply its current width only if it is expanded
            Object.assign(style, {
                minWidth: minWidth ? `${minWidth}px` : "",
                width: width ? `${width}px` : "",
                paddingBottom: paddingBottom ? `${paddingBottom}px` : ""
            });
        }

        return (
            <div
                ref={this.handleBaseRef}
                class={classnames}
                data-selector={this.props["data-selector"]}
                style={style}
                onMouseDown={onMouseDown}
                onClick={onClick}>
                <div class={classname(styles.content, contentClass, {
                    [styles.noScroll]: noScroll,
                    [styles.allowOverflow]: allowOverflow
                })} ref={this.handleContentRef}>
                    {children}
                </div>
                {!disableResize && (
                    <div class={styles.resizerContainer} ref={noderef(this, "resizer")} onMouseDown={stopPropagation}>
                        <div class={styles.resizer}/>
                    </div>
                )}
            </div>
        );
    }
}

export default Sidebar;
